import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = [
    'displayLabel',
    'labelText',
    'dropdown',
    'searchInput',
    'optionsContainer',
    'scrollableContainer',
    'loading',
    'noResults',
    'hiddenInputsContainer',
    'selectedCount'
  ]

  static values = {
    endpoint: String,
    valueField: String,
    labelField: String,
    placeholder: String,
    clauseIndex: Number,
    fieldName: String
  }

  connect() {
    // Manually read values from dataset because Stimulus has trouble with double-dash identifiers
    const dataset = this.element.dataset
    this._endpoint = dataset['select-MultiSearchableSelectEndpointValue'] || dataset.selectMultiSearchableSelectEndpointValue || ''
    this._valueField = dataset['select-MultiSearchableSelectValueFieldValue'] || dataset.selectMultiSearchableSelectValueFieldValue || 'id'
    this._labelField = dataset['select-MultiSearchableSelectLabelFieldValue'] || dataset.selectMultiSearchableSelectLabelFieldValue || 'name'
    this._placeholder = dataset['select-MultiSearchableSelectPlaceholderValue'] || dataset.selectMultiSearchableSelectPlaceholderValue || 'Type to search...'
    this._clauseIndex = parseInt(dataset['select-MultiSearchableSelectClauseIndexValue'] || dataset.selectMultiSearchableSelectClauseIndexValue || '0')
    this._fieldName = dataset['select-MultiSearchableSelectFieldNameValue'] || dataset.selectMultiSearchableSelectFieldNameValue || ''
    this._dependentParam = dataset.dependentParam || ''
    this._dependentParamName = dataset.dependentParamName || ''
    this._otherDependentParam = dataset.otherDependentParam || ''
    this._otherDependentParamName = dataset.otherDependentParamName || ''

    this.selectedItems = new Map()
    this.options = []
    this.isOpen = false
    this.searchTimer = null
    this.shouldSortOnNextRender = false // Flag to sort selected items to top only on dropdown open

    // Infinite scrolling state
    this.currentPage = 1
    this.hasNextPage = false
    this.loadingMore = false
    this.onScrollHandler = null

    // Store dependent listeners for cleanup
    this.dependentListeners = []

    if (this.hasHiddenInputsContainerTarget) {
      const existingInputs = this.hiddenInputsContainerTarget.querySelectorAll('input[type="hidden"]')
      existingInputs.forEach(input => {
        const label = input.dataset.label || input.value
        this.selectedItems.set(input.value, label)
      })

      // Update display label and count after reading existing selections
      this.updateDisplayLabel()
      this.updateSelectedCount()
    }

    this.boundHandleClickOutside = this.handleClickOutside.bind(this)

    this.localOptions = []
    if (this.element.dataset.options) {
      try {
        this.localOptions = JSON.parse(this.element.dataset.options)
      } catch (e) {
        console.error('Error parsing options:', e)
      }
    }

    if (this._endpoint && (this._dependentParam || this._otherDependentParam)) {
      this.setupDependentListeners()
    }
  }

  setupDependentListeners() {
    const listen = (param) => {
      const el = document.querySelector(`[data-select-key="${param}"] select`)
      if (el) {
        // Create a bound handler function that we can reference for removal
        const handler = () => {
          this.selectedItems.clear()
          this.options = []
          this.updateDisplayLabel()
          this.updateSelectedCount()
          this.updateHiddenInputs()
          if (this.hasOptionsContainerTarget) this.optionsContainerTarget.innerHTML = ''
          this.notifyFilterChange()
        }

        // Store the element and handler for cleanup
        this.dependentListeners.push({ element: el, handler })
        el.addEventListener('change', handler)
      }
    }
    if (this._dependentParam) listen(this._dependentParam)
    if (this._otherDependentParam) listen(this._otherDependentParam)
  }

  addDependentParams(url) {
    const add = (p, n) => {
      const el = document.querySelector(`[data-select-key="${p}"] select`)
      if (el?.value) url.searchParams.set(n, el.value)
    }
    if (this._dependentParam) add(this._dependentParam, this._dependentParamName)
    if (this._otherDependentParam) add(this._otherDependentParam, this._otherDependentParamName)
  }

  disconnect() {
    // Clean up position update listeners
    if (this.positionUpdateHandler) {
      window.removeEventListener('scroll', this.positionUpdateHandler, true)
      if (this.positionUpdateFrame) {
        cancelAnimationFrame(this.positionUpdateFrame)
        this.positionUpdateFrame = null
      }
      this.positionUpdateHandler = null
    }

    // Clean up keyboard listener
    if (this.keyboardHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.keyboardHandler)
      this.keyboardHandler = null
    }

    // Clean up dependent listeners
    if (this.dependentListeners) {
      this.dependentListeners.forEach(({ element, handler }) => {
        element.removeEventListener('change', handler)
      })
      this.dependentListeners = []
    }

    this.removeClickOutsideListener()
    this.destroyDropdownScrollListener()
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }
  }

  toggleDropdown(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.isOpen) {
      this.hideDropdown()
    } else {
      this.showDropdown()
    }
  }

  showDropdown() {
    if (this.isOpen) return
    if (!this.hasDropdownTarget) return

    this.isOpen = true

    // Close sibling dropdowns (other searchable and multi-searchable selects)
    this.closeSiblingDropdowns()

    // Position dropdown using fixed positioning
    this.updateDropdownPosition()

    // Show dropdown immediately
    this.dropdownTarget.classList.remove('d-none')

    // Add scroll listener with requestAnimationFrame for smooth repositioning
    this.positionUpdateHandler = () => {
      if (this.positionUpdateFrame) {
        cancelAnimationFrame(this.positionUpdateFrame)
      }
      this.positionUpdateFrame = requestAnimationFrame(() => {
        this.updateDropdownPosition()
      })
    }
    window.addEventListener('scroll', this.positionUpdateHandler, true)

    // Listen for keyboard close/open to reposition
    if (window.visualViewport) {
      this.keyboardHandler = () => this.updateDropdownPosition()
      window.visualViewport.addEventListener('resize', this.keyboardHandler)
    }

    setTimeout(() => {
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.focus()
      }
    }, 50)

    // Set flag to sort selected items to top on initial render
    this.shouldSortOnNextRender = true

    if (this.options.length === 0) {
      if (this._endpoint && this._endpoint.trim() !== '' && this._endpoint !== 'undefined') {
        this.showLoading()
        this.performSearchImmediate('')
      } else if (this.localOptions.length > 0) {
        this.options = this.localOptions
        this.renderOptions()
      } else {
        this.showNoResults()
      }
    } else {
      this.renderOptions()
    }

    this.updateSelectedCount()

    // Setup infinite scroll listener
    this.setupDropdownScrollListener()

    setTimeout(() => {
      document.addEventListener('click', this.boundHandleClickOutside, true)
    }, 50)
  }

  hideDropdown() {
    if (!this.isOpen) return

    this.isOpen = false

    // Remove position update listeners
    if (this.positionUpdateHandler) {
      window.removeEventListener('scroll', this.positionUpdateHandler, true)
      if (this.positionUpdateFrame) {
        cancelAnimationFrame(this.positionUpdateFrame)
        this.positionUpdateFrame = null
      }
      this.positionUpdateHandler = null
    }

    // Remove keyboard listener
    if (this.keyboardHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.keyboardHandler)
      this.keyboardHandler = null
    }

    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('d-none')
    }
    this.destroyDropdownScrollListener()
    this.removeClickOutsideListener()
  }

  closeSiblingDropdowns() {
    // Find parent container (values dropdown or similar)
    const container = this.element.closest('[data-target*="valuesDropdown"]') ||
      this.element.closest('.p-3') ||
      this.element.parentElement?.parentElement

    if (!container) return

    // Close searchable-select dropdowns
    container.querySelectorAll('[data-controller*="searchable-select"]:not([data-controller*="multi-searchable-select"])').forEach(el => {
      const ctrl = this.application.getControllerForElementAndIdentifier(el, 'select--searchable-select')
      if (ctrl?.isOpen) ctrl.hideDropdown()
    })

    // Close other multi-searchable-select dropdowns
    container.querySelectorAll('[data-controller*="multi-searchable-select"]').forEach(el => {
      if (el !== this.element) {
        const ctrl = this.application.getControllerForElementAndIdentifier(el, 'select--multi-searchable-select')
        if (ctrl?.isOpen) ctrl.hideDropdown()
      }
    })
  }

  updateDropdownPosition() {
    if (!this.displayLabelTarget || !this.dropdownTarget) return

    // Check if keyboard is open (visual viewport is smaller than layout viewport)
    const isKeyboardOpen = window.visualViewport &&
      window.visualViewport.height < window.innerHeight - 100

    // Don't reposition when keyboard is open to prevent coordinate system mismatch
    if (isKeyboardOpen) return

    const rect = this.displayLabelTarget.getBoundingClientRect()
    const viewportWidth = window.innerWidth

    // Match width to the display label
    this.dropdownTarget.style.width = `${rect.width}px`

    // Position dropdown below parent
    this.dropdownTarget.style.top = `${rect.bottom + 1}px`

    // Get dropdown width (use current width or rect width)
    const dropdownWidth = this.dropdownTarget.offsetWidth || rect.width
    let leftPosition = rect.left

    // Check if dropdown would overflow right edge
    if (leftPosition + dropdownWidth > viewportWidth - 20) {
      // Align to right edge with padding
      leftPosition = viewportWidth - dropdownWidth - 20
    }

    // Ensure dropdown doesn't overflow left edge
    if (leftPosition < 20) {
      leftPosition = 20
    }

    this.dropdownTarget.style.left = `${leftPosition}px`
  }

  handleClickOutside(event) {
    // Check if click is inside the multi-select component or the parent pill dropdown
    const clickedInsideComponent = this.element.contains(event.target)
    const clickedInsideParentDropdown = event.target.closest('[data-target*="filter-pill.valuesDropdown"]')

    if (!clickedInsideComponent && !clickedInsideParentDropdown) {
      this.hideDropdown()
    }
  }

  removeClickOutsideListener() {
    document.removeEventListener('click', this.boundHandleClickOutside, true)
  }

  performSearch(event) {
    const query = event ? event.target.value : ''

    // Clear any existing search timer
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }

    // Debounce search: wait 400ms after user stops typing
    this.searchTimer = setTimeout(async () => {
      // Reset pagination for new search
      this.currentPage = 1
      this.hasNextPage = false

      // If query is cleared (empty), re-sort selected items to top
      if (query.trim() === '') {
        this.shouldSortOnNextRender = true
      }

      if (this._endpoint) {
        // Fetch from endpoint
        await this.fetchOptions(query)
      } else if (this.localOptions.length > 0) {
        // Use local options
        this.filterLocalOptions(query)
      }

      this.renderOptions()
    }, 400)
  }

  async performSearchImmediate(query) {
    // Immediate search without debouncing (used for initial load)
    // Reset pagination
    this.currentPage = 1
    this.hasNextPage = false

    // If query is empty or cleared, re-sort selected items to top
    if (!query || query.trim() === '') {
      this.shouldSortOnNextRender = true
    }

    if (this._endpoint) {
      // Fetch from endpoint
      await this.fetchOptions(query)
    } else if (this.localOptions.length > 0) {
      // Use local options
      this.filterLocalOptions(query)
    }

    this.renderOptions()
  }

  async fetchOptions(query) {
    this.showLoading()

    try {
      const url = new URL(this._endpoint, window.location.origin)

      url.searchParams.set('type', 'select2')
      url.searchParams.set('page', this.currentPage)
      if (query) url.searchParams.set('q', query)
      this.addDependentParams(url)
      const response = await fetch(url)

      // Check if response is OK (status 200-299)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      // Extract options and pagination info
      const parsed = this.extractOptionsFromResponse(data)
      this.options = parsed.options
      this.hasNextPage = parsed.hasMore

      this.hideLoading()
    } catch (error) {
      console.error('Multi-select fetch error:', error)
      // Clear options on error
      this.options = []
      this.hasNextPage = false
      this.hideLoading()
      this.showError('Failed to load options')
    }
  }

  filterLocalOptions(query) {
    const lowerQuery = query.toLowerCase()

    this.options = this.localOptions.filter(option => {
      const label = option[this._labelField] || option.name || ''
      return label.toLowerCase().includes(lowerQuery)
    })
  }

  renderOptions() {
    if (this.options.length === 0) {
      this.showNoResults()
      return
    }

    this.hideNoResults()

    // Only sort selected items to top when dropdown is first opened
    // This prevents items from jumping around while user is actively selecting
    if (this.shouldSortOnNextRender) {
      // Find selected items that aren't in the current options (from other pages)
      const optionValues = new Set(
        this.options.map(opt => String(this._valueField in opt ? opt[this._valueField] : opt.id || opt.value || ''))
      )

      const missingSelectedItems = []
      this.selectedItems.forEach((label, value) => {
        if (!optionValues.has(value)) {
          // Create a placeholder option for selected items not in current page
          const placeholder = {
            id: value,
            name: label,
            text: label,
            label: label,
            value: value
          }
          // Set the custom field names as well
          placeholder[this._valueField] = value
          placeholder[this._labelField] = label
          missingSelectedItems.push(placeholder)
        }
      })

      // Prepend missing selected items to options array
      if (missingSelectedItems.length > 0) {
        this.options = [...missingSelectedItems, ...this.options]
      }

      // Sort options: selected items first, then unselected items
      // This creates the Linear app-like behavior where checked items appear at the top
      this.options = [...this.options].sort((a, b) => {
        const aValue = String(this._valueField in a ? a[this._valueField] : a.id || a.value || '')
        const bValue = String(this._valueField in b ? b[this._valueField] : b.id || b.value || '')
        const aSelected = this.selectedItems.has(aValue)
        const bSelected = this.selectedItems.has(bValue)

        // Both selected or both unselected: maintain original order
        if (aSelected === bSelected) return 0
        // Selected items come first
        return aSelected ? -1 : 1
      })
      // Reset flag after sorting
      this.shouldSortOnNextRender = false
    }

    const html = this.options.map(option => {
      // Handle different API response formats
      // Try value_field first, then common alternatives
      const rawValue = this._valueField in option ? option[this._valueField] :
        'id' in option ? option.id :
          'value' in option ? option.value : ''
      const value = String(rawValue)
      const label = option[this._labelField] || option.name || option.text || option.label || value
      const isSelected = this.selectedItems.has(value)

      return `
        <div class="d-flex align-items-center px-3 py-2 ${isSelected ? 'bg-light' : ''}"
             style="cursor: pointer; user-select: none; transition: all 0.15s ease-in-out;"
             onmouseover="if(!this.classList.contains('bg-light')) this.style.backgroundColor='#f8f9fa';"
             onmouseout="if(!this.classList.contains('bg-light')) this.style.backgroundColor='';">
          <input type="checkbox"
                 ${isSelected ? 'checked' : ''}
                 class="mr-2"
                 data-action="click->select--multi-searchable-select#toggleOption"
                 data-value="${value}"
                 data-label="${label}" />
          <span class="flex-grow-1"
                data-action="click->select--multi-searchable-select#selectAndClose"
                data-value="${value}"
                data-label="${label}">${label}</span>
        </div>
      `
    }).join('')

    this.optionsContainerTarget.innerHTML = html

    // Setup infinite scroll listener after rendering
    if (this._endpoint) {
      this.setupDropdownScrollListener()
    }
  }

  // Select option and close both dropdowns (triggered by clicking on option text)
  selectAndClose(event) {
    event.stopPropagation()
    const element = event.currentTarget
    const value = element.dataset.value
    const label = element.dataset.label

    // Toggle selection (same as toggleOption)
    if (this.selectedItems.has(value)) {
      this.selectedItems.delete(value)
    } else {
      this.selectedItems.set(value, label)
    }

    this.renderOptions()
    this.updateDisplayLabel()
    this.updateSelectedCount()
    this.updateHiddenInputs()
    this.notifyFilterChange()

    // Close this dropdown
    this.hideDropdown()

    // Close the parent pill dropdown
    const pillDropdown = this.element.closest('[data-target*="filter-pill.valuesDropdown"]')
    if (pillDropdown) {
      const pillElement = pillDropdown.closest('[data-controller*="select--filter-pill"]')
      if (pillElement) {
        const pillController = this.application.getControllerForElementAndIdentifier(
          pillElement,
          'select--filter-pill'
        )
        if (pillController && pillController.closeAllSegmentDropdowns) {
          pillController.closeAllSegmentDropdowns()
        }
      }
    }
  }

  toggleOption(event) {
    event.stopPropagation()
    const element = event.currentTarget
    const value = element.dataset.value
    const label = element.dataset.label

    if (this.selectedItems.has(value)) {
      this.selectedItems.delete(value)
    } else {
      this.selectedItems.set(value, label)
    }

    this.renderOptions()
    this.updateDisplayLabel()
    this.updateSelectedCount()
    this.updateHiddenInputs()

    // Auto-notify changes (no Apply button needed)
    this.notifyFilterChange()
  }

  updateDisplayLabel() {
    if (!this.hasLabelTextTarget) return
    const count = this.selectedItems.size
    if (count === 0) {
      this.labelTextTarget.textContent = this._placeholder || 'Type to search...'
      this.labelTextTarget.classList.add('text-muted')
    } else if (count === 1) {
      const label = Array.from(this.selectedItems.values())[0]
      const maxLabelLength = 22
      let displayText
      if (label.length > maxLabelLength) {
        displayText = label.substring(0, maxLabelLength) + '...'
      } else {
        displayText = label
      }
      this.labelTextTarget.textContent = displayText
      this.labelTextTarget.classList.remove('text-muted')
    } else {
      const firstLabel = Array.from(this.selectedItems.values())[0]
      const suffix = `, +${count - 1} more`
      const maxLabelLength = 13
      let displayText
      if (firstLabel.length > maxLabelLength) {
        displayText = firstLabel.substring(0, maxLabelLength) + '...' + suffix
      } else {
        displayText = firstLabel + suffix
      }
      this.labelTextTarget.textContent = displayText
      this.labelTextTarget.classList.remove('text-muted')
    }
  }

  updateHiddenInputs() {
    if (!this.hasHiddenInputsContainerTarget) return

    // Clear existing hidden inputs
    this.hiddenInputsContainerTarget.innerHTML = ''

    // Create new hidden inputs for tracking only (no name attribute)
    // The actual form submission fields are created by handleMultiSelectApplied event handler
    const selectedArray = Array.from(this.selectedItems.entries())

    selectedArray.forEach(([value, label], index) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.value = value
      input.dataset.label = label
      this.hiddenInputsContainerTarget.appendChild(input)
    })
  }

  updateSelectedCount() {
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = this.selectedItems.size
    }
  }

  removeItem(event) {
    event.stopPropagation()
    const value = event.currentTarget.dataset.value
    this.selectedItems.delete(value)
    this.updateDisplayLabel()
    this.updateSelectedCount()
    this.updateHiddenInputs()
    this.renderOptions()
    this.notifyFilterChange()
  }

  clearAll(event) {
    event.preventDefault()
    event.stopPropagation()
    this.selectedItems.clear()
    this.updateDisplayLabel()
    this.updateSelectedCount()
    this.updateHiddenInputs()
    this.renderOptions()
    this.notifyFilterChange()
  }

  notifyFilterChange() {
    // Trigger custom event for advanced filter controller to handle
    const changeEvent = new CustomEvent('multi-select-applied', {
      detail: {
        clauseIndex: this._clauseIndex,
        selectedItems: Array.from(this.selectedItems.entries())
      },
      bubbles: true
    })
    this.element.dispatchEvent(changeEvent)
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
    }
    this.hideNoResults()
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
    }
  }

  showNoResults() {
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.remove('d-none')
    }
    if (this.hasOptionsContainerTarget) {
      this.optionsContainerTarget.innerHTML = ''
    }
  }

  hideNoResults() {
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('d-none')
    }
  }

  showError(message) {
    // Clear options array to ensure no stale data
    this.options = []

    // Hide loading and no-results messages
    this.hideLoading()
    this.hideNoResults()

    // Show error message in options container
    if (this.hasOptionsContainerTarget) {
      this.optionsContainerTarget.innerHTML = `
        <div class="px-3 py-2 text-danger small">${message}</div>
      `
    }
  }

  setupDropdownScrollListener() {
    if (!this.isOpen || !this._endpoint) return

    // Use the scrollable container (the one with overflow-y: auto)
    const container = this.hasScrollableContainerTarget ? this.scrollableContainerTarget : null
    if (!container) return

    this.destroyDropdownScrollListener()

    if (!this.hasNextPage) return

    this.onScrollHandler = () => {
      if (!this.hasNextPage || this.loadingMore) return

      // Trigger load when scrolled near bottom (50px threshold)
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
        this.loadMoreOptions()
      }
    }

    container.addEventListener('scroll', this.onScrollHandler)

    // Auto-load if content doesn't fill the container
    if (container.scrollHeight <= container.clientHeight && this.hasNextPage && !this.loadingMore) {
      this.loadMoreOptions()
    }
  }

  destroyDropdownScrollListener() {
    const container = this.hasScrollableContainerTarget ? this.scrollableContainerTarget : null
    if (container && this.onScrollHandler) {
      container.removeEventListener('scroll', this.onScrollHandler)
    }
    this.onScrollHandler = null
  }

  async loadMoreOptions() {
    if (this.loadingMore || !this.hasNextPage) return

    this.loadingMore = true
    const nextPage = (this.currentPage || 1) + 1

    try {
      const url = new URL(this._endpoint, window.location.origin)
      url.searchParams.set('type', 'select2')
      url.searchParams.set('page', nextPage)

      const query = this.searchInputTarget?.value || ''
      if (query.trim()) {
        url.searchParams.set('q', query.trim())
      }
      this.addDependentParams(url)
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      const parsed = this.extractOptionsFromResponse(data)

      if (parsed.options && parsed.options.length) {
        const container = this.hasScrollableContainerTarget ? this.scrollableContainerTarget : null
        const scrollTop = container ? container.scrollTop : 0

        // Create a map of existing option values to their index
        const existingValuesMap = new Map()
        this.options.forEach((opt, idx) => {
          const value = String(this._valueField in opt ? opt[this._valueField] : opt.id || opt.value || '')
          existingValuesMap.set(value, idx)
        })

        // Process new options: replace placeholders or add new ones
        parsed.options.forEach(newOpt => {
          const value = String(this._valueField in newOpt ? newOpt[this._valueField] : newOpt.id || newOpt.value || '')

          if (existingValuesMap.has(value)) {
            // Replace placeholder with real item (preserves position, updates data)
            const existingIndex = existingValuesMap.get(value)
            this.options[existingIndex] = newOpt
          } else {
            // Add new option if not duplicate
            this.options.push(newOpt)
          }
        })

        // Re-render all options
        this.renderOptions()

        // Restore scroll position
        if (container) {
          container.scrollTop = scrollTop
        }

        // Update pagination state
        this.currentPage = nextPage
        this.hasNextPage = parsed.hasMore
      } else {
        this.hasNextPage = false
      }
    } catch (error) {
      console.error('Load more failed:', error)
    } finally {
      this.loadingMore = false
    }
  }

  extractOptionsFromResponse(data) {
    let options = []
    let hasMore = false

    if (Array.isArray(data)) {
      // Simple array format
      options = data
      hasMore = false
    } else if (data) {
      // Check for different response formats
      if (Array.isArray(data.results)) {
        options = data.results
        const p = data.pagination || {}
        hasMore = !!(p.more || p.has_more || p.hasNextPage)
      } else if (Array.isArray(data.data)) {
        options = data.data
        const m = data.meta || {}
        hasMore = !!(m.next_page || m.nextPage ||
          (m.current_page && m.total_pages && m.current_page < m.total_pages) ||
          data.has_next_page || data.has_more)
      } else if (Array.isArray(data.items)) {
        options = data.items
        hasMore = !!(data.next_page || data.nextPage || data.has_next_page)
      }
    }

    return { options, hasMore }
  }
}

